import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import {
  FormatSize as FormatSizeIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';

/**
 * FontSizeSelector Component
 *
 * Dropdown menu for the app-wide font scale (display density) --
 * Small/Medium/Large/Extra Large. Persists per-browser via ThemeContext
 * (same pattern as ThemeSelector) so it's still applied next login.
 *
 * @param {object} props
 * @param {string} props.size - Icon button size ('small', 'medium', 'large')
 */
const FontSizeSelector = ({ size = 'medium' }) => {
  const { colors, fontScale, setFontScale, fontScaleOptions } = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (scale) => {
    setFontScale(scale);
    handleClose();
  };

  const current = fontScaleOptions.find((o) => o.scale === fontScale);

  return (
    <>
      <Tooltip title={`Font size: ${current?.label || 'Medium'}`}>
        <IconButton
          onClick={handleClick}
          size={size}
          sx={{
            color: colors.text?.secondary || colors.textSecondary,
            '&:hover': {
              color: colors.text?.primary || colors.textPrimary,
              bgcolor: colors.hover?.background,
            },
          }}
          aria-label="font size selector"
          aria-controls={open ? 'font-size-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <FormatSizeIcon />
        </IconButton>
      </Tooltip>

      <Menu
        id="font-size-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'font-size-selector-button' }}
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border?.default || colors.border}`,
            minWidth: 200,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: colors.text?.muted || colors.textMuted, fontWeight: 600, textTransform: 'uppercase' }}
          >
            Font Size
          </Typography>
        </Box>
        <Divider sx={{ borderColor: colors.border?.default || colors.border }} />

        {fontScaleOptions.map((opt) => {
          const isSelected = opt.scale === fontScale;
          return (
            <MenuItem
              key={opt.id}
              onClick={() => handleSelect(opt.scale)}
              selected={isSelected}
              sx={{
                py: 1,
                '&.Mui-selected': {
                  bgcolor: colors.success?.subtle,
                  '&:hover': { bgcolor: colors.success?.subtleHover },
                },
                '&:hover': { bgcolor: colors.hover?.background },
              }}
            >
              <ListItemIcon sx={{ color: isSelected ? colors.success?.main : (colors.text?.secondary || colors.textSecondary) }}>
                <FormatSizeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={opt.label}
                primaryTypographyProps={{
                  sx: { color: colors.text?.primary || colors.textPrimary, fontWeight: isSelected ? 600 : 400 },
                }}
              />
              {isSelected && (
                <CheckIcon fontSize="small" sx={{ color: colors.success?.main, ml: 1 }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default FontSizeSelector;
